<template>
  <div
    v-if="isMobileFiltersOpen"
    class="tw:fixed tw:inset-0 tw:top-0 tw:z-51 tw:w-full tw:h-full tw:overflow-hidden tw:flex tw:overflow-y-auto tw:z-100"
  >
    <div
      class="tw:mx-auto tw:w-full tw:max-w-[980px] tw:bg-white tw:m-auto box-shadow tw:relative"
      v-click-outside="closeMobileFilters"
    >
      <div
        class="tw:fixed tw:z-50 tw:w-full tw:h-[72px] tw:bg-white tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-3.5 tw:px-8 tw:text-gray-800"
      >
        <div>
          <button
            class="tw:outline-hidden tw:pr-2 tw:pt-2"
            @click="closeMobileFilters"
            @keydown.enter="closeMobileFilters"
          >
            <Close class="tw:size-2.5" />
          </button>
        </div>
        <div class="tw:font-bold">{{ $t('filters') }}</div>
        <div>
          <button
            class="tw:text-blue-700 tw:font-bold tw:disabled:text-gray-400"
            :disabled="!hasChanges"
            @click="resetTempFilters"
          >
            {{ $t('rc_filter_reset') }}
          </button>
        </div>
      </div>

      <div class="tw:flex tw:flex-col tw:mb-[76px] tw:mt-[72px] tw:min-h-screen tw:p-5">
        <MobileSelectTimeframeFilter
          type="mobile"
          name="timeframes"
          :label="$t('tbs_invoice_timeframe')"
          :options="timeframeOptions"
          :value="temporaryFiltersObject['timeframe']"
          :startDate="temporaryFiltersObject['startDate']"
          :endDate="temporaryFiltersObject['endDate']"
          @onSelect="onSelectTempTimeframe"
          @onStartDateSelect="onSelectTempCustomStartDate"
          @onEndDateSelect="onSelectTempCustomEndDate"
        />
        <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
        <MobileSelectProductFilter
          type="mobile"
          name="products"
          :label="$t('tbs_product_filter')"
          :options="productsOptions"
          :value="temporaryFiltersObject['products']"
          @onSelect="onSelectTempProducts"
        />
        <div v-if="hasPropertiesFilter" class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
        <MobileSelectPropertyFilter
          v-if="hasPropertiesFilter"
          type="mobile"
          :label="$t('hotels')"
          :options="propertiesOptions"
          name="properties"
          :value="temporaryFiltersObject['properties']"
          @onSelect="onSelectTempPropertyFilters"
        />
        <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
        <MobileSelectStatusFilter
          type="mobile"
          name="status"
          :label="$t('tbs_invoice_status')"
          :options="statusOptions"
          :value="temporaryFiltersObject['status']"
          @onSelect="onSelectTempStatus"
        />
      </div>

      <div
        class="footer shadow tw:fixed tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white"
      >
        <div class="tw:md:flex tw:lg:hidden tw:w-full">
          <MainButton class="tw:w-full" @click="applyFilters" :disabled="!hasChanges">{{ $t('apply') }}</MainButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, watchEffect } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import Close from '@/components/icons/Close.svg?component'
import MobileSelectTimeframeFilter from '@/components/billing/invoices/filters/MobileSelectTimeframeFilter.vue'
import MobileSelectProductFilter from '@/components/billing/invoices/filters/MobileSelectProductFilter.vue'
import MobileSelectStatusFilter from '@/components/billing/invoices/filters/MobileSelectStatusFilter.vue'
import MobileSelectPropertyFilter from '@/components/billing/premiumProducts/filters/MobileSelectPropertyFilter.vue'

import { useInvoicesFilters } from '@/components/billing/invoices/useInvoicesFilters'
import { isEqual, cloneDeep } from '@/utils/Utility'

const { isMobileFiltersOpen, closeMobileFilters, startDate, endDate, selectedFiltersAmount } = useInvoicesFilters()

const emit = defineEmits(['applyFilters'])
const temporaryFiltersObject = ref({})
const applyFilters = () => emit('applyFilters', changes.value, false, 'mobile')

const props = defineProps({
  mobileFiltersOpen: Boolean,
  timeframeOptions: Array,
  timeframeValue: Object,
  productsOptions: Array,
  productsValue: Array,
  statusOptions: Array,
  statusValues: Array,
  propertiesOptions: Array,
  propertiesValues: Array,
  hasPropertiesFilter: Boolean,
})

const defaultResetState = {
  timeframe: { id: 'last-6-months', label: 'last_6_months', value: '6' },
  startDate: null,
  endDate: null,
  products: [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }],
  properties: [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }],
  status: [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }],
}

const initialFilters = ref({})

const toYearMonth = dateString => {
  return dateString.split('-').slice(0, 2).join('-')
}

const snapshotCurrentState = () => {
  return {
    timeframe: cloneDeep(props.timeframeValue),
    startDate: startDate.value !== null ? cloneDeep(toYearMonth(startDate.value)) : null,
    endDate: endDate.value !== null ? cloneDeep(toYearMonth(endDate.value)) : null,
    products: cloneDeep(props.productsValue ?? defaultResetState.products),
    status: cloneDeep(props.statusValues),
    properties: cloneDeep(props.propertiesValues),
  }
}

initialFilters.value = snapshotCurrentState()
temporaryFiltersObject.value = cloneDeep(initialFilters.value)

watchEffect(() => {
  if (props.productsValue) {
    temporaryFiltersObject.value['products'] = cloneDeep(props.productsValue)

    initialFilters.value['products'] = cloneDeep(props.productsValue)
  }
})

const resetTempFilters = () => {
  emit('applyFilters', defaultResetState, true, 'mobile')

  temporaryFiltersObject.value = cloneDeep(defaultResetState)
  initialFilters.value = cloneDeep(defaultResetState)
  selectedFiltersAmount.value = 0
}

const onSelectTempTimeframe = newTimeframe => {
  temporaryFiltersObject.value['timeframe'] = newTimeframe
}
const onSelectTempCustomStartDate = newStartDate => {
  temporaryFiltersObject.value['startDate'] = newStartDate
}
const onSelectTempCustomEndDate = newEndDate => {
  temporaryFiltersObject.value['endDate'] = newEndDate
}

const onSelectTempProducts = product => {
  const isAll = product.id === 0
  const hasAll = temporaryFiltersObject.value['products'].some(p => p.id === 0)

  if (isAll) {
    if (
      !temporaryFiltersObject.value['products'].find(p => p.id === 0) ||
      temporaryFiltersObject.value['products'].length > 1
    ) {
      temporaryFiltersObject.value['products'] = [product]
    }
    return
  }

  let newSelection
  if (hasAll) {
    newSelection = [product]
  } else {
    const exists = temporaryFiltersObject.value['products'].find(p => p.id === product.id)
    if (exists) {
      newSelection = temporaryFiltersObject.value['products'].filter(p => p.id !== product.id)
      if (newSelection.length === 0) return
    } else {
      newSelection = [...temporaryFiltersObject.value['products'], product]
    }
  }

  temporaryFiltersObject.value['products'] = newSelection
}

const onSelectTempStatus = status => {
  const isAllOption = status.id === 0 || status.value === 'all'
  const hasAll = temporaryFiltersObject.value['status'].some(item => item.id === 0 || item.value === 'all')
  const exists = temporaryFiltersObject.value['status'].some(item => item.id === status.id)

  const allStatusOptions = props.statusOptions || []
  const totalRegularOptions = allStatusOptions.filter(opt => opt.id !== 0).length
  const allOption = allStatusOptions.find(opt => opt.id === 0)

  if (isAllOption) {
    if (!exists) {
      temporaryFiltersObject.value['status'] = [status]
    } else if (temporaryFiltersObject.value['status'].length === 1) {
      return
    }
    return
  }

  let newSelection

  if (hasAll) {
    newSelection = [status]
  } else if (exists) {
    newSelection = temporaryFiltersObject.value['status'].filter(item => item.id !== status.id)
    if (newSelection.length === 0) return
  } else {
    newSelection = [...temporaryFiltersObject.value['status'], status]
  }

  const regularSelectedCount = newSelection.filter(item => item.id !== 0).length

  if (regularSelectedCount === totalRegularOptions) {
    temporaryFiltersObject.value['status'] = [allOption]
  } else {
    temporaryFiltersObject.value['status'] = newSelection
  }
}

const onSelectTempPropertyFilters = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = temporaryFiltersObject.value['properties'].some(item => item.id === 0)

  if (isAllOption) {
    temporaryFiltersObject.value['properties'] = [newValue]
  } else {
    if (currentHasAll) {
      temporaryFiltersObject.value['properties'] = temporaryFiltersObject.value['properties'].filter(
        item => item.id !== 0
      )
    }

    const index = temporaryFiltersObject.value['properties'].findIndex(item => item.id === newValue.id)
    if (index > -1) {
      temporaryFiltersObject.value['properties'].splice(index, 1)

      if (temporaryFiltersObject.value['properties'].length === 0) {
        temporaryFiltersObject.value['properties'] = [
          { value: 'all', label: 'campaign_subscription_status_all', id: 0 },
        ]
      }
    } else {
      temporaryFiltersObject.value['properties'].push(newValue)
    }
  }
}

const hasChanges = computed(() => {
  return !isEqual(temporaryFiltersObject.value, initialFilters.value)
})

const changes = computed(() => {
  const changedFilters = {}

  Object.keys(temporaryFiltersObject.value).forEach(key => {
    if (!isEqual(temporaryFiltersObject.value[key], initialFilters.value[key])) {
      changedFilters[key] = temporaryFiltersObject.value[key]
    }
  })

  return changedFilters
})
</script>
